import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ScheduleIcon from '@material-ui/icons/Schedule';
import PlaceIcon from '@material-ui/icons/Place';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import Alert from '@material-ui/core/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTheme } from '@material-ui/core/styles';
import { registerTallerUser } from '../../../../services/userService';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';


const INVERSION_USD = 25;
const EMAIL_TALLER = 'academy@tnonetwork.live';

const InversionRegistro = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const tallerSlug = (pathname || '').replace(/^\/+|\/+$/g, '').split('/')[0] || 'tallerproduccion';
  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (status.type) setStatus({ type: null, message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: null, message: '' });
    const result = await registerTallerUser({ ...form, tallerSlug });
    setLoading(false);
    if (result.success) {
      setStatus({
        type: 'success',
        message: '¡Inscripción registrada! Revisa tu correo para próximos pasos. Puedes contactarnos en ' + EMAIL_TALLER + ' para el pago de la inversión.',
      });
      setForm({ name: '', lastName: '', email: '', phone: '', notes: '' });
    } else {
      setStatus({ type: 'error', message: result.error || 'Error al registrar. Intenta de nuevo.' });
    }
  };

  return (
    <Box>
      <Typography
        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
        variant="overline"
        color="primary"
        align="center"
        display="block"
        gutterBottom
      >
        💳 Inversión y registro
      </Typography>
      <Box
        component={Typography}
        fontWeight={700}
        variant="h4"
        gutterBottom
        align="center"
        marginBottom={1}
      >
        ¡Es momento de dar el salto de oyente a creador!
      </Box>
      <Typography
        variant="body1"
        align="center"
        color="textSecondary"
        sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}
      >
        Inversión de <strong>{INVERSION_USD} USD</strong>. Completa el formulario y asegura tu cupo.
      </Typography>
      <Box
        sx={{
          maxWidth: 420,
          mx: 'auto',
          mb: 3,
          py: 2,
          px: 2,
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
        }}
      >
        <List disablePadding>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AttachMoneyIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Inversión" secondary={`${INVERSION_USD} USD`} />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ScheduleIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Duración"
              secondary="5 horas de inmersión total (8:00 AM - 1:00 PM)"
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PlaceIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Lugar" secondary="TNO Media Lab / Online" />
          </ListItem>
        </List>
      </Box>

      <Box component="form" onSubmit={handleSubmit} maxWidth={440} mx="auto">
        {status.type === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
            {status.message}
          </Alert>
        )}
        {status.type === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
            {status.message}
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            required
            fullWidth
            label="Nombre"
            value={form.name}
            onChange={handleChange('name')}
            variant="outlined"
            size="medium"
          />
          <TextField
            required
            fullWidth
            label="Apellido"
            value={form.lastName}
            onChange={handleChange('lastName')}
            variant="outlined"
            size="medium"
          />
          <TextField
            required
            fullWidth
            type="email"
            label="Correo electrónico"
            value={form.email}
            onChange={handleChange('email')}
            variant="outlined"
            size="medium"
          />
          <TextField
            fullWidth
            label="Teléfono (opcional)"
            value={form.phone}
            onChange={handleChange('phone')}
            variant="outlined"
            size="medium"
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentarios (opcional)"
            value={form.notes}
            onChange={handleChange('notes')}
            variant="outlined"
            size="medium"
            placeholder="Ej. modalidad preferida (presencial/online)"
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Registrando…' : 'Inscribirme al taller'}
          </Button>
        </Box>
        <Typography variant="caption" color="textSecondary" display="block" align="center" sx={{ mt: 2 }}>
          Tras registrarte, te contactaremos a {EMAIL_TALLER} para confirmar tu cupo y el pago de {INVERSION_USD} USD.
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" mt={3}>
        <Button
          href="https://www.tnonetwork.com"
          variant="outlined"
          color="primary"
          size="large"
          target="_blank"
          rel="noopener noreferrer"
        >
          Conocer TNO Network
        </Button>
      </Box>
      <PriceVatDisclaimer />
    </Box>
  );
};

export default InversionRegistro;
